import { createContext, useContext, useState, useEffect } from 'react';
import { parseApiError } from '../utils/apiErrorParser';

const rawBaseUrl = process.env.REACT_APP_API_BASE_URL;
const BASE_URL =
  rawBaseUrl && rawBaseUrl !== 'undefined' && rawBaseUrl !== 'null'
    ? rawBaseUrl.replace(/\/+$/, '')
    : '';
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null); // New role state
  const [anchorId, setAnchorId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedRole = localStorage.getItem('authRole'); // Load role
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    }
    if (storedRole) {
      setRole(storedRole);
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      if (!BASE_URL) {
        throw new Error('Missing REACT_APP_API_BASE_URL configuration');
      }

      const response = await fetch(`${BASE_URL}/eci/apis/v4/auth/generate_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const text = await response.text();
      let res = null;
      try {
        res = text ? JSON.parse(text) : null;
      } catch {
        throw new Error(
          'Login failed: the server did not return JSON (often wrong REACT_APP_API_BASE_URL or the API returned an HTML error page).'
        );
      }

      if (!response.ok) {
        throw new Error(parseApiError(res, `Login failed (${response.status})`));
      }

      if (!res || !res.metadata?.api_status || !res.msg?.token) {
        throw new Error(parseApiError(res, 'Login failed'));
      }

      const token = res.msg.token;
      const role = res.msg.role || null; // Get role
      const anchorId = res.msg.anchor_id || null;

      setToken(token);
      setRole(role);
      setAnchorId(anchorId);
      setIsAuthenticated(true);
      localStorage.setItem('authToken', token);
      localStorage.setItem('authRole', role); // Save role
      localStorage.setItem('anchorId', anchorId);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.message);
      setIsAuthenticated(false);
      setToken(null);
      setRole(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authRole');
      localStorage.removeItem('anchorId');
      return { success: false, message: error.message };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setRole(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    localStorage.removeItem('anchorId');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, token, role, anchorId, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
